/**
 * channel-groups.ts — Gom tài khoản kênh thành nhóm để lọc.
 *
 * Mã nền tảng là số, khai báo ở backend `src/shared/constants.ts`. Nhân viên
 * không nhớ số, họ nghĩ theo "Zalo cá nhân", "OA", "Facebook", "TikTok" —
 * bảng này dịch từ số sang cách nghĩ đó. Mã nhóm KHỚP backend
 * `src/shared/channel-groups.ts` (danh sách hội thoại và báo cáo nhận `channel=<mã nhóm>`).
 *
 * Nhóm "Khác" cố ý tồn tại: Pancake khác và các kênh thêm sau này
 * không thuộc nhóm nào ở trên, bỏ đi thì tài khoản biến mất khỏi bảng chọn.
 */

import { i18n } from '@/lib/i18n'

/** Mã nền tảng — khớp `Platform` trong backend. */
export const PLATFORM = {
  ZALO_OA: 1,
  ZALO_USER: 2,
  ZALO_BOT: 3,
  FACEBOOK_PAGE: 10,
  INSTAGRAM: 11,
  TELEGRAM: 12,
  TELEGRAM_BUSINESS: 13,
  WEBCHAT: 20,
  PANCAKE_FB: 30,
  PANCAKE_IG: 31,
  PANCAKE_TIKTOK: 32,
  PANCAKE_OTHER: 39,
  TIKTOK_SHOP: 40,
  SHOPEE: 41,
} as const

export type ChannelGroupId =
  | 'zalo_user' | 'zalo_oa' | 'zalo_bot' | 'facebook' | 'tiktok' | 'shopee' | 'telegram' | 'webchat' | 'other'

export interface ChannelGroup {
  id: ChannelGroupId
  label: string
  /** Mã nền tảng thuộc nhóm. Rỗng = nhóm hứng phần còn lại. */
  platforms: number[]
}

/** Thứ tự hiển thị bám theo tần suất dùng: Zalo cá nhân là kênh chính của TDVN. */
export const CHANNEL_GROUPS: ChannelGroup[] = [
  { id: 'zalo_user', get label() { return i18n.t('common:channels.zaloPersonal') }, platforms: [PLATFORM.ZALO_USER] },
  { id: 'zalo_oa', label: 'Zalo OA', platforms: [PLATFORM.ZALO_OA] },
  { id: 'zalo_bot', label: 'Zalo Bot', platforms: [PLATFORM.ZALO_BOT] },
  {
    id: 'facebook',
    label: 'Facebook',
    // Instagram và Pancake FB/IG đều là kênh Meta — nhân viên coi chung một mối.
    platforms: [PLATFORM.FACEBOOK_PAGE, PLATFORM.INSTAGRAM, PLATFORM.PANCAKE_FB, PLATFORM.PANCAKE_IG],
  },
  // TikTok qua Pancake và TikTok Shop chính thức — với nhân viên đều là "TikTok".
  { id: 'tiktok', label: 'TikTok', platforms: [PLATFORM.PANCAKE_TIKTOK, PLATFORM.TIKTOK_SHOP] },
  { id: 'shopee', label: 'Shopee', platforms: [PLATFORM.SHOPEE] },
  // Bot Telegram và tài khoản cá nhân gắn bot qua Telegram Business.
  { id: 'telegram', label: 'Telegram', platforms: [PLATFORM.TELEGRAM, PLATFORM.TELEGRAM_BUSINESS] },
  { id: 'webchat', label: 'Web chat', platforms: [PLATFORM.WEBCHAT] },
  { id: 'other', get label() { return i18n.t('common:channels.other') }, platforms: [] },
]

/** Nhóm của một tài khoản. Không khớp nhóm nào thì về "Khác". */
export function groupOfPlatform(platform?: number | null): ChannelGroupId {
  if (platform == null) return 'other'
  const g = CHANNEL_GROUPS.find((x) => x.platforms.includes(platform))
  return g ? g.id : 'other'
}

/**
 * Các nhóm THỰC SỰ có tài khoản, theo thứ tự của CHANNEL_GROUPS — hiện chip
 * cho nhóm rỗng chỉ khiến người dùng bấm vào rồi thấy danh sách trống.
 */
export function groupsPresent(platforms: Iterable<number | null | undefined>): ChannelGroup[] {
  const ids = new Set<ChannelGroupId>()
  for (const p of platforms) ids.add(groupOfPlatform(p))
  return CHANNEL_GROUPS.filter((g) => ids.has(g.id))
}
